import * as React from 'react';
import { usePage } from '@inertiajs/react';
import { assetUrl } from '../../utils/assetUrl';
import { route } from '../../utils/route';
import { trackSignupClicked } from '../../utils/tracking';

interface VerduHeroProps {
  loginUrl: string;
  whatsappUrl: string;
  promoDiscount: number;
}

export function VerduHero({ whatsappUrl, promoDiscount }: VerduHeroProps) {
  const { cdnUrl } = usePage<{ cdnUrl: string }>().props;
  const heroImg = assetUrl(cdnUrl, '/img/verdu-hero.webp');

  return (
    <section className="verdu-hero">
      <style>{`
        .verdu-hero {
          position: relative;
          background: url(${heroImg}) center center/cover no-repeat;
          padding: 100px 0 110px;
          min-height: 480px;
          display: flex;
          align-items: center;
        }
        .verdu-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.65);
        }
        .verdu-hero-inner {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
          padding: 0 24px;
          text-align: center;
        }
        .verdu-hero h1 {
          font-size: 38px;
          font-weight: 800;
          color: #fff;
          margin: 0 0 16px;
          line-height: 1.2;
        }
        .verdu-hero .hero-sub {
          font-size: 20px;
          color: rgba(255,255,255,0.9);
          margin: 0 0 12px;
          font-weight: 500;
        }
        .verdu-hero .hero-desc {
          font-size: 17px;
          color: rgba(255,255,255,0.75);
          margin: 0 0 36px;
        }
        .verdu-hero-ctas {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .verdu-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: #1EAA25;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s;
        }
        .verdu-btn-primary:hover {
          background: #178f1e;
          color: #fff;
          text-decoration: none;
        }
        .verdu-btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          background: transparent;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: 2px solid rgba(255,255,255,0.6);
          border-radius: 8px;
          text-decoration: none;
          transition: all 0.2s;
        }
        .verdu-btn-secondary:hover {
          background: rgba(255,255,255,0.1);
          border-color: #fff;
          color: #fff;
          text-decoration: none;
        }
        .verdu-hero-promo {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 20px;
          padding: 10px 20px;
          background: rgba(30, 170, 37, 0.2);
          border: 1px solid rgba(30, 170, 37, 0.5);
          border-radius: 8px;
          color: #6ee674;
          font-size: 15px;
          font-weight: 500;
        }
        .verdu-hero-promo strong {
          color: #fff;
          font-weight: 700;
        }
        .verdu-hero-whatsapp {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 16px;
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          text-decoration: none;
          transition: color 0.2s;
        }
        .verdu-hero-whatsapp:hover {
          color: #25d366;
          text-decoration: none;
        }
        @media (max-width: 640px) {
          .verdu-hero {
            padding: 70px 0 80px;
            min-height: auto;
          }
          .verdu-hero h1 {
            font-size: 28px;
          }
          .verdu-hero .hero-sub {
            font-size: 17px;
          }
          .verdu-hero-ctas {
            flex-direction: column;
            align-items: stretch;
          }
          .verdu-btn-primary, .verdu-btn-secondary {
            justify-content: center;
          }
          .verdu-hero-promo {
            display: block;
            text-align: center;
          }
        }
      `}</style>
      <div className="verdu-hero-inner">
        <h1>Dejá de hacer shopping en el Mercado de Abasto</h1>
        <p className="hero-sub">Las verdulerías que usan Abasto Virtual ahorran 15hs semanales en sus compras.</p>
        <p className="hero-desc">Recibí frutas y verduras mayoristas en tu local, sin madrugar ni recorrer puestos.</p>
        <div className="verdu-hero-ctas">
          <a href={route('wholesale.pricing')} className="verdu-btn-primary">
            <i className="fa fa-list-alt" /> Ver Precios Mayoristas
          </a>
          <a href={route('company.create')} className="verdu-btn-secondary" onClick={() => trackSignupClicked('verdulerias', 'hero')}>
            <i className="fa fa-user-plus" /> Crear una cuenta mayorista
          </a>
        </div>
        <div className="verdu-hero-promo">
          <i className="fa fa-star" /> Tu primer mes con <strong>{promoDiscount}% de descuento</strong> en todos los productos
        </div>
        <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="verdu-hero-whatsapp">
          <i className="fa fa-whatsapp" /> ¿Dudas? Escribinos por WhatsApp
        </a>
      </div>
    </section>
  );
}
